import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, ExternalLink, Plus, Search, Store } from 'lucide-react';
import { RESTAURANT_STATUSES, RESTAURANT_STATUS_LABEL, type RestaurantStatus } from '@garlic/shared';
import { api } from '../lib/api';
import { formatDate } from '../lib/format';
import { Alert, Button, Card, EmptyState, Input, PageHeader, Select, Spinner } from '../ui/primitives';
import { StatusBadge } from './shared';

type Row = {
  id: string; slug: string; name: string; status: RestaurantStatus; city: string | null; state: string | null;
  createdAt: string; plan: string | null; staffCount: number; url: string; panelUrl: string;
};

export function RestaurantList() {
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get('q') ?? '');
  const status = params.get('status') ?? '';
  const page = Number(params.get('page') ?? 1);
  const query = useQuery({
    queryKey: ['dev-restaurants', params.toString()],
    queryFn: () => api<{ total: number; page: number; pageSize: number; items: Row[] }>(`/api/dev/restaurants?${params.toString()}`),
    placeholderData: keepPreviousData,
  });
  const update = (patch: Record<string, string>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) v ? next.set(k, v) : next.delete(k);
    if (!('page' in patch)) next.delete('page');
    setParams(next, { replace: true });
  };
  const pages = query.data ? Math.max(1, Math.ceil(query.data.total / query.data.pageSize)) : 1;

  return (
    <>
      <PageHeader
        title="Restaurantes"
        description={query.data ? `${query.data.total} cadastrado(s)` : undefined}
        action={
          <Link to="/dev/restaurantes/novo">
            <Button icon={<Plus className="size-4" />}>Novo restaurante</Button>
          </Link>
        }
      />
      <Card>
        <form
          className="flex flex-col gap-3 border-b border-line p-4 sm:flex-row"
          onSubmit={(e) => {
            e.preventDefault();
            update({ q });
          }}
        >
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-3" aria-hidden />
            <Input aria-label="Buscar por nome ou endereço" placeholder="Buscar por nome ou endereço" className="pl-9" value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          <Select aria-label="Filtrar por status" className="sm:w-48" value={status} onChange={(e) => update({ status: e.target.value })}>
            <option value="">Todos os status</option>
            {RESTAURANT_STATUSES.map((s) => (
              <option key={s} value={s}>{RESTAURANT_STATUS_LABEL[s]}</option>
            ))}
          </Select>
        </form>

        {query.isPending && <Spinner />}
        {query.isError && <div className="p-4"><Alert tone="danger">Não foi possível carregar a lista.</Alert></div>}
        {query.data && query.data.items.length === 0 && (
          <EmptyState icon={<Store className="size-5" />} title={params.toString() ? 'Nenhum restaurante encontrado' : 'Nenhum restaurante cadastrado'}>
            {params.toString() ? 'Tente outro termo ou status.' : 'Cadastre o primeiro estabelecimento para começar.'}
          </EmptyState>
        )}
        {query.data && query.data.items.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="text-xs uppercase tracking-wide text-ink-3">
                <tr className="border-b border-line">
                  <th className="px-4 py-3 font-medium">Restaurante</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="hidden px-4 py-3 font-medium md:table-cell">Plano</th>
                  <th className="hidden px-4 py-3 font-medium md:table-cell">Equipe</th>
                  <th className="hidden px-4 py-3 font-medium lg:table-cell">Desde</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {query.data.items.map((r) => (
                  <tr key={r.id} className="hover:bg-paper/70">
                    <td className="px-4 py-3">
                      <Link to={`/dev/restaurantes/${r.id}`} className="font-medium text-ink hover:text-garlic-600">{r.name}</Link>
                      <div className="text-xs text-ink-3">
                        {r.slug}
                        {r.city ? ` · ${r.city}/${r.state ?? ''}` : ''}
                      </div>
                    </td>
                    <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
                    <td className="hidden px-4 py-3 md:table-cell">{r.plan ?? '—'}</td>
                    <td className="hidden px-4 py-3 tabular-nums md:table-cell">{r.staffCount}</td>
                    <td className="hidden px-4 py-3 text-ink-3 lg:table-cell">{formatDate(r.createdAt)}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-right">
                      <a href={r.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-medium text-garlic-600 hover:underline" title={r.url}>
                        Cardápio <ExternalLink className="size-3" />
                      </a>
                      <a href={r.panelUrl} target="_blank" rel="noreferrer" className="ml-3 inline-flex items-center gap-1 text-xs font-medium text-ink-2 hover:underline" title={r.panelUrl}>
                        Painel <ExternalLink className="size-3" />
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {query.data && pages > 1 && (
          <div className="flex items-center justify-between border-t border-line px-4 py-3 text-sm">
            <span className="text-ink-3">Página {page} de {pages}</span>
            <div className="flex gap-2">
              <Button variant="secondary" size="sm" disabled={page <= 1} onClick={() => update({ page: String(page - 1) })} icon={<ChevronLeft className="size-4" />}>Anterior</Button>
              <Button variant="secondary" size="sm" disabled={page >= pages} onClick={() => update({ page: String(page + 1) })}>Próxima <ChevronRight className="size-4" /></Button>
            </div>
          </div>
        )}
      </Card>
    </>
  );
}
